import React, { forwardRef } from 'react';
import { Line } from 'react-chartjs-2';

const EXTRA_SERIES_COLOURS = {
  'Cash': 'perf_cash_line',
};

const EXTRA_SERIES_DEFAULTS = {
  'perf_cash_line': '#f0a500',
};

const PerformanceChart = forwardRef(({ data, colours = {} }, ref) => {
  if (!data || !data.portfolio || data.portfolio.length === 0) {
    return <div className="chart-placeholder">No performance data available</div>;
  }

  const portfolioColour = colours.perf_portfolio_line || '#072040';
  const benchmarkColour = colours.perf_benchmark_line || '#599BE3';

  const datasets = [
    {
      label: 'Portfolio',
      data: data.portfolio.map(p => ({ x: p.date, y: p.value })),
      borderColor: portfolioColour,
      backgroundColor: 'transparent',
      borderWidth: 2,
      pointRadius: 0,
      tension: 0.1,
    },
    {
      label: data.benchmark_name || 'Benchmark',
      data: data.benchmark.map(p => ({ x: p.date, y: p.value })),
      borderColor: benchmarkColour,
      backgroundColor: 'transparent',
      borderWidth: 2,
      pointRadius: 0,
      tension: 0.1,
      borderDash: [5, 5],
    },
  ];

  // Add extra series (Cash overlay on the DDP chart)
  if (data.extra_series) {
    data.extra_series.forEach((series) => {
      const colourKey = EXTRA_SERIES_COLOURS[series.name];
      const colour = (colourKey && colours[colourKey])
        || (colourKey && EXTRA_SERIES_DEFAULTS[colourKey])
        || '#888888';
      datasets.push({
        label: series.name,
        data: series.data.map(p => ({ x: p.date, y: p.value })),
        borderColor: colour,
        backgroundColor: 'transparent',
        borderWidth: 1.5,
        pointRadius: 0,
        tension: 0.1,
        borderDash: [5, 5],
      });
    });
  }

  const chartData = { datasets };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: 0 },
    interaction: { intersect: false, mode: 'index' },
    plugins: {
      legend: {
        position: 'bottom',
        align: 'center',
        labels: { font: { size: 10 }, boxWidth: 20, boxHeight: 2, padding: 5 },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${(ctx.parsed.y * 100).toFixed(1)}%`,
        },
      },
    },
    scales: {
      x: {
        type: 'time',
        time: { unit: 'month', tooltipFormat: 'MMM yyyy' },
        border: { display: false },
        grid: { display: false },
        ticks: { font: { size: 10 }, maxTicksLimit: 8, maxRotation: 0, minRotation: 0 },
      },
      y: {
        border: { display: false },
        grid: { color: 'rgba(0,0,0,0.1)' },
        ticks: {
          font: { size: 10 },
          callback: (val) => `${(val * 100).toFixed(0)}%`,
        },
      },
    },
  };

  return (
    <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
      <Line ref={ref} data={chartData} options={options} />
    </div>
  );
});

PerformanceChart.displayName = 'PerformanceChart';
export default PerformanceChart;
